import React, { useState, useEffect } from 'react';
import { Database, Plus, Trash2, Download, Loader2, RefreshCw, AlertCircle, Check, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminBackups = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchBackups = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/phase2/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups || []);
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  const createBackup = async () => {
    const token = localStorage.getItem('token');
    setCreating(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/admin/phase2/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Backup created successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchBackups();
    } catch (err) {
      console.error('Error creating backup:', err);
      setError(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;

    const token = localStorage.getItem('token');
    setDeleting(backupId);
    setError(null);

    try {
      await axios.delete(`${API_URL}/api/admin/phase2/backup/${backupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Backup deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchBackups();
    } catch (err) {
      console.error('Error deleting backup:', err);
      setError(err.response?.data?.detail || 'Failed to delete backup');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-backups">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backup & Restore</h1>
          <p className="text-gray-600 mt-1">Create and manage database snapshots</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchBackups}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={createBackup}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 disabled:opacity-50 transition-colors"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Create Backup
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {backups.length === 0 ? (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No backups yet</h3>
            <p className="text-gray-500 mb-4">Create your first backup to get started</p>
            <button
              onClick={createBackup}
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 disabled:opacity-50 transition-colors"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Create First Backup
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {backups.map((backup) => (
              <div
                key={backup.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid={`backup-item-${backup.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">Backup #{backup.id}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        backup.status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {backup.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(backup.created_at)}
                      </span>
                      <span>•</span>
                      <span>{backup.type}</span>
                    </div>
                    {backup.collections && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(backup.collections).map(([name, count]) => (
                          <span 
                            key={name} 
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                          >
                            {name}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => deleteBackup(backup.id)}
                    disabled={deleting === backup.id}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete backup"
                    data-testid={`delete-backup-${backup.id}`}
                  >
                    {deleting === backup.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-medium text-amber-900 mb-2">Important Notes</h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• Backups capture a snapshot of database record counts</li>
          <li>• For full data recovery, use the Data Export feature</li>
          <li>• Automated daily backups are recommended for production</li>
          <li>• Contact support for full database restore operations</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminBackups;
